import {
  Button,
  Checkbox,
  Flex,
  Icon,
  Input,
  InputGroup,
  InputRightAddon,
  Text,
  useToast,
} from "@chakra-ui/react";
import { t } from "i18next";
import { cloneDeep, isEqual } from "lodash";
import { useCallback, useState } from "react";
import { FaCheck, FaPlus } from "react-icons/fa";
import {
  DefaultNotificationSettingsDto,
  FrameworkAgreementRole,
  FrameworkValueReminderDto,
  OrganizationRoleType,
} from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { MultiSelector } from "../../../common/input/Selector/MultiSelector";
import { PersonsSelector } from "../../../common/persons/PersonsSelector";
import { useSaveOrganization } from "../../organizations/current/useOrganizationEditor";
import { organizationContractRoles } from "../../organizations/current/manage-users/utils";
import { frameworkValueReminderFailure } from "./frameworkValueReminderFailure";
import { frameworkValueReminders, initSettings } from "./NotificationSettings";
import { useReceiverRoleNames } from "./useReceiverRoleNames";

type FrameworkValueReminder = Omit<FrameworkValueReminderDto, "percentage"> & {
  percentage?: number;
};

const initSetting = (): FrameworkValueReminder => ({
  percentage: undefined,
  receivers: {
    persons: [],
    organizationRoles: [],
    frameworkAgreementRoles: [FrameworkAgreementRole.Responsible],
  },
});

export const isValidPercentage = (
  percentage: number | undefined,
): percentage is number =>
  percentage !== undefined &&
  Number.isInteger(percentage) &&
  percentage >= 1 &&
  percentage <= 100;

export const reachesSomeone = ({
  receivers,
}: Pick<FrameworkValueReminder, "receivers">): boolean =>
  receivers.persons.length > 0 ||
  receivers.organizationRoles.length > 0 ||
  receivers.frameworkAgreementRoles.length > 0;

const isFrameworkValueReminderDto = (
  setting: FrameworkValueReminder,
): setting is FrameworkValueReminderDto =>
  isValidPercentage(setting.percentage) && reachesSomeone(setting);

export const FrameworkValueReminderSettingForm = () => {
  const authState = useLoggedInWithOrgContextState();
  const toast = useToast();

  const { saveOrganization, isSaving } = useSaveOrganization();

  const saveSetting = useCallback(
    async (setting: FrameworkValueReminderDto): Promise<boolean> => {
      const settings: DefaultNotificationSettingsDto =
        cloneDeep(authState.selectedOrg.defaultNotificationSettings) ??
        initSettings();
      settings.frameworkAgreementNotificationSettings = {
        valueNotifications: [...frameworkValueReminders(settings), setting],
      };
      try {
        await saveOrganization({ defaultNotificationSettings: settings });
        return true;
      } catch (e) {
        toast(
          frameworkValueReminderFailure(
            t("The reminder was not saved"),
            e,
          ),
        );
        return false;
      }
    },
    [
      authState.selectedOrg.defaultNotificationSettings,
      saveOrganization,
      toast,
    ],
  );

  return (
    <FrameworkValueReminderForm
      organizationId={authState.selectedOrg.id}
      existing={frameworkValueReminders(
        authState.selectedOrg.defaultNotificationSettings,
      )}
      isSaving={isSaving}
      onSave={saveSetting}
    />
  );
};

export const FrameworkValueReminderForm = ({
  organizationId,
  existing,
  isSaving,
  onSave,
  isOpen,
  onClose,
}: {
  organizationId: string;
  existing: FrameworkValueReminderDto[];
  isSaving: boolean;
  onSave: (reminder: FrameworkValueReminderDto) => Promise<boolean>;
  isOpen?: boolean;
  onClose?: () => void;
}) => {
  const toast = useToast();
  const roleName = useReceiverRoleNames();
  const [isOpenHere, setIsOpenHere] = useState(false);
  const [setting, setSetting] = useState<FrameworkValueReminder>(initSetting());
  const isVisible = isOpen ?? isOpenHere;
  const close = () => {
    setSetting(initSetting());
    if (onClose) onClose();
    else setIsOpenHere(false);
  };

  const save = async () => {
    if (!isFrameworkValueReminderDto(setting))
      throw Error("Invalid framework value reminder setting!");
    if (existing.find((s) => isEqual(s, setting))) {
      toast({
        description: t("This setting already exists"),
        variant: "subtle",
        status: "error",
      });
      return;
    }
    if (await onSave(setting)) {
      close();
    }
  };

  if (!isVisible && isOpen !== undefined) return null;

  if (!isVisible)
    return (
      <Flex>
        <Button
          rightIcon={<Icon as={FaPlus} />}
          size="xs"
          variant={"ghost"}
          colorScheme="teal"
          onClick={() => setIsOpenHere(true)}
        >
          {t("Create new")}
        </Button>
      </Flex>
    );

  const percentageIsInvalid =
    setting.percentage !== undefined && !isValidPercentage(setting.percentage);

  return (
    <Flex
      position="relative"
      zIndex="2"
      flexDirection="column"
      rowGap="2"
      border="1px solid"
      borderColor="smBorder"
      rounded="md"
      p="2"
    >
      <Flex flexDirection="column" rowGap="1">
        <InputGroup width="30%" minWidth="40" size="md">
          <Input
            type="number"
            rounded="md"
            placeholder={`${t("Share of the ceiling value")}...`}
            min={1}
            max={100}
            step={1}
            isInvalid={percentageIsInvalid}
            value={setting.percentage === undefined ? "" : setting.percentage}
            onChange={(e) => {
              const updatedSetting = cloneDeep(setting);
              updatedSetting.percentage =
                e.target.value === "" ? undefined : +e.target.value;
              setSetting(updatedSetting);
            }}
          />
          <InputRightAddon>%</InputRightAddon>
        </InputGroup>
        {percentageIsInvalid && (
          <Text fontSize="sm" color="smError">
            {t("Enter a whole number from 1 to 100")}
          </Text>
        )}
      </Flex>
      <PersonsSelector
        size="md"
        organizationIds={[organizationId]}
        selectedPersons={setting.receivers.persons}
        roles={organizationContractRoles}
        onChange={(persons) => {
          const updatedSetting = cloneDeep(setting);
          updatedSetting.receivers.persons = persons;
          setSetting(updatedSetting);
        }}
        placeholder={`${t("Select individual receivers")}...`}
      />
      <MultiSelector
        value={setting.receivers.organizationRoles.map((role) => ({
          label: roleName.organizationRole(role),
          value: role,
        }))}
        options={organizationContractRoles.map((role) => ({
          label: roleName.organizationRole(role),
          value: role,
        }))}
        noOptionsAvailableMessage={`${t("No roles found")}...`}
        onChange={(options) => {
          const updatedSetting = cloneDeep(setting);
          updatedSetting.receivers.organizationRoles = options.map(
            (o) => o.value as OrganizationRoleType,
          );
          setSetting(updatedSetting);
        }}
        placeholder={{
          text: `${t("Select receivers by role in organization")}...`,
          color: "",
        }}
      />
      <Checkbox
        isChecked={setting.receivers.frameworkAgreementRoles.includes(
          FrameworkAgreementRole.Responsible,
        )}
        onChange={(e) => {
          const updatedSetting = cloneDeep(setting);
          updatedSetting.receivers.frameworkAgreementRoles = e.target.checked
            ? [FrameworkAgreementRole.Responsible]
            : [];
          setSetting(updatedSetting);
        }}
      >
        <Text fontSize="sm">
          {t("Responsible for the framework agreement")}
        </Text>
      </Checkbox>
      {!reachesSomeone(setting) && (
        <Text fontSize="sm" color="smError">
          {t("Choose at least one receiver")}
        </Text>
      )}
      <Text fontSize="sm" color="smMuted">
        {t(
          "Only receivers who may see all of the framework agreement's call-offs are notified.",
        )}
      </Text>
      <Flex columnGap="2">
        <Button
          colorScheme="teal"
          size={"xs"}
          rightIcon={<Icon as={FaCheck} />}
          isLoading={isSaving}
          isDisabled={!isFrameworkValueReminderDto(setting)}
          onClick={() => save()}
        >
          {t("Save")}
        </Button>
        <Button
          size={"xs"}
          variant="ghost"
          isDisabled={isSaving}
          onClick={close}
        >
          {t("Cancel")}
        </Button>
      </Flex>
    </Flex>
  );
};
